/**
 * Theme application + source resolution.
 *
 * The active theme is written to `<html data-theme="...">`; the CSS files in
 * src/styles/themes/ react to that attribute. This module owns *when* and
 * *to what* that attribute is set.
 *
 * Source-of-truth precedence (first match wins):
 *   1. URL override   — `?theme=<id>` (preview / QA; also persisted below)
 *   2. Local override  — localStorage `ptv2:theme` (lets a user preview a
 *                        theme on their own machine without backend changes)
 *   3. Backend config  — the instance-wide `ui.theme` value (applied later,
 *                        once loaded; see applyThemeFromConfig)
 *   4. DEFAULT_THEME_ID
 *
 * NOTE: production should be driven by (3). The backend `GET /api/v1/configs`
 * endpoint is currently admin-only, so a public/whitelisted read path is
 * still needed before every user can receive the instance theme — that is a
 * contract item, tracked separately. Until then (1)/(2) make the glass theme
 * fully previewable.
 */
import { DEFAULT_THEME_ID, toThemeId, type ThemeId } from './themes';

export const THEME_STORAGE_KEY = 'ptv2:theme';

/** Write the theme attribute that the theme stylesheets key off. */
export function applyTheme(id: ThemeId): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = id;
}

/** The theme currently applied to <html>, coerced to a known id. */
export function getActiveTheme(): ThemeId {
  if (typeof document === 'undefined') return DEFAULT_THEME_ID;
  return toThemeId(document.documentElement.dataset.theme);
}

function readUrlOverride(): ThemeId | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = new URLSearchParams(window.location.search).get('theme');
    return value !== null ? toThemeId(value) : null;
  } catch {
    return null;
  }
}

function readLocalOverride(): ThemeId | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value !== null ? toThemeId(value) : null;
  } catch {
    return null;
  }
}

/** Persist a local override (used by a future in-app theme switcher / QA). */
export function setLocalThemeOverride(id: ThemeId): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, id);
  } catch {
    // ignore storage failures (private mode / quota)
  }
  applyTheme(id);
}

export function clearLocalThemeOverride(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Resolve and apply the best theme we can determine synchronously, before
 * first paint, to avoid a flash of the wrong theme. A URL `?theme=` is also
 * persisted so it survives client-side navigation.
 */
export function initTheme(): ThemeId {
  const urlOverride = readUrlOverride();
  if (urlOverride !== null) {
    setLocalThemeOverride(urlOverride);
    return urlOverride;
  }
  const resolved = readLocalOverride() ?? DEFAULT_THEME_ID;
  applyTheme(resolved);
  return resolved;
}

/**
 * Apply the instance theme coming from the backend `ui.theme` config value,
 * UNLESS the user has a local/URL override active (overrides win so previews
 * are sticky). Call this once the config has loaded.
 */
export function applyThemeFromConfig(configValue: string | null | undefined): void {
  if (readLocalOverride() !== null) return; // local preview wins
  applyTheme(toThemeId(configValue));
}
